import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

interface BaselineDropdownProps {
  availableBaselines: string[];
  selectedBaseline: string | null;
  onBaselineChange: (baseline: string | null) => void;
  label?: string;
  className?: string;
  displayStyle?: 'dropdown' | 'select'; // Allow both styles
}

/**
 * Reusable baseline selection component with two display styles:
 * - 'select': Native HTML select element (simpler, better for small screens)
 * - 'dropdown': Custom dropdown with truncation (better for long labels)
 */
export const BaselineDropdown: React.FC<BaselineDropdownProps> = ({
  availableBaselines,
  selectedBaseline,
  onBaselineChange,
  label = 'Baseline',
  className = '',
  displayStyle = 'select',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const currentBaseline = selectedBaseline || availableBaselines[0] || '';

  useEffect(() => {
    if (displayStyle === 'dropdown') {
      const handleClickOutside = (event: MouseEvent) => {
        // Explicitly exclude the button from click-outside detection
        // This prevents race conditions with the button's onClick toggle
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current !== event.target &&
          !buttonRef.current?.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [displayStyle]);

  const handleSelect = (baseline: string) => {
    onBaselineChange(baseline);
    setIsOpen(false);
  };

  // Native select element style
  if (displayStyle === 'select') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
          {label}
        </label>
        <select
          value={currentBaseline}
          onChange={(e) => onBaselineChange(e.target.value)}
          className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 border-0 focus:outline-none whitespace-nowrap"
        >
          {availableBaselines.map((baseline) => (
            <option key={baseline} value={baseline}>
              {baseline}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Custom dropdown style
  return (
    <div className={`flex items-center gap-2 ${className}`} ref={dropdownRef}>
      <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
        {label}
      </label>
      <div className="relative">
        <button
          ref={buttonRef}
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 justify-between whitespace-nowrap max-w-[200px]"
        >
          <span className="truncate">
            {currentBaseline.length > 15
              ? `${currentBaseline.substring(0, 12)}...`
              : currentBaseline}
          </span>
          <ChevronDown
            className={`h-3 w-3 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div className="absolute top-full mt-1 right-0 min-w-[200px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-[9999] max-h-60 overflow-y-auto">
            {availableBaselines.map((baseline) => (
              <button
                key={baseline}
                onClick={() => handleSelect(baseline)}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                  baseline === currentBaseline
                    ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    : 'text-gray-600 dark:text-gray-300'
                }`}
              >
                <span className="truncate block">
                  {baseline.length > 30 ? `${baseline.substring(0, 27)}...` : baseline}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
